import cn from 'clsx';
import { LeadScoreDetailsItem } from './LeadScoreDetailsItem';
import { formatDistanceToNow, isValid } from 'date-fns';
import { LeadScoreProperty } from 'crono-fe-common/types/leadScore';
import { createHashedObject } from 'crono-fe-common/utils/object';
import {
  getColorsByScoreRange,
  getIconByScoreChange,
  getScoreChange,
  getScoreRange,
  getTypeLabel,
} from 'crono-fe-common/components/LeadScore/lead-score.constants';
import {
  Button,
  DotSpinner,
  IconButton,
  RefreshSmIcon,
  SettingsSmIcon,
} from '@crono-one/design-system';
import { ScoreSettingsTypes } from 'crono-fe-common/types/DTO/patchScoringSettings';
import { TLeadScore } from 'crono-fe-common/types/leadScore';
import React, { PropsWithChildren, useMemo } from 'react';

interface ILeadScoreDetailsContentProps {
  details: TLeadScore['score'];
  mdScore: number;
  highScore: number;
  type: ScoreSettingsTypes;
  onRefresh: () => void;
  onSettings: () => void;
  hideSettings?: boolean;
  defaultProperties?: LeadScoreProperty[];
  refreshLoading: boolean;
  loading: boolean;
}

function normalizeDetailScores(details: TLeadScore['score']) {
  const scoreDetails = details?.scoreInstance?.details ?? [];
  const gainedPoints = details?.scoreInstance?.gainedPoints ?? 0;

  const totalScore = Number(
    details?.currentScoreNoSignificant ?? details?.currentScore,
  );

  const normalizedMap = new Map<number, number>();

  if (!scoreDetails.length || !gainedPoints || !Number.isFinite(gainedPoints)) {
    scoreDetails.forEach((detail) => normalizedMap.set(detail.id, 0));
    return normalizedMap;
  }

  const rawScores = scoreDetails.map((detail, index) => {
    const rawValue = (totalScore * detail.pointsAdded) / gainedPoints;
    const intPart = Math.trunc(rawValue);
    const decimalPart = rawValue - intPart;

    return {
      detailId: detail.id,
      index,
      intPart,
      decimalPart,
    };
  });

  const intSum = rawScores.reduce((sum, item) => sum + item.intPart, 0);
  const diff = Math.round(totalScore - intSum);
  const increments = Math.max(0, diff);

  const sortedByDecimal = [...rawScores].sort((a, b) => {
    if (b.decimalPart === a.decimalPart) return a.index - b.index;
    return b.decimalPart - a.decimalPart;
  });

  sortedByDecimal.forEach((item, idx) => {
    const finalScore = item.intPart + (idx < increments ? 1 : 0);
    normalizedMap.set(item.detailId, finalScore);
  });

  return normalizedMap;
}

export const LeadScoreDetailsContent: React.FC<
  PropsWithChildren<ILeadScoreDetailsContentProps>
> = ({
  details,
  type,
  mdScore,
  highScore,
  onRefresh,
  onSettings,
  hideSettings,
  defaultProperties,
  refreshLoading,
  children,
}) => {
  const typeLabel = getTypeLabel(type);
  const scoreRange = getScoreRange(details?.currentScore, mdScore, highScore);
  const scoreColors = getColorsByScoreRange(scoreRange);
  const scoreChange = getScoreChange(details?.lastScore, details?.currentScore);
  const scoreChangeIcon = getIconByScoreChange(scoreChange, 'md');

  const hashedDefaultProperties = useMemo(() => {
    return createHashedObject(defaultProperties, 'propertyKey');
  }, [defaultProperties]);

  const isInsignificant = (
    idx: number,
    negativeReplyIndex?: number,
    optOutIndex?: number,
    positiveReplyIndex?: number,
  ) => {
    const significantIndices = [
      negativeReplyIndex,
      optOutIndex,
      positiveReplyIndex,
    ].filter((value) => value !== undefined && value !== -1);

    if (!significantIndices.length) return false;

    return !significantIndices.includes(idx);
  };

  const scoreDetailsSortedBySignificance = useMemo(() => {
    return [...(details?.scoreInstance?.details ?? [])].sort((a, b) => {
      const negativeReplyIndex = details?.scoreInstance?.details?.findIndex(
        (detail) => detail.scoreDetailType === 'NegativeReplies',
      );
      const optOutIndex = details?.scoreInstance?.details?.findIndex(
        (detail) => detail.scoreDetailType === 'OptOut',
      );
      const positiveReplyIndex = details?.scoreInstance?.details?.findIndex(
        (detail) => detail.scoreDetailType === 'PositiveReplies',
      );

      if (
        isInsignificant(
          a.id,
          negativeReplyIndex,
          optOutIndex,
          positiveReplyIndex,
        )
      )
        return -1;
      if (
        isInsignificant(
          b.id,
          negativeReplyIndex,
          optOutIndex,
          positiveReplyIndex,
        )
      )
        return 1;
      return 0;
    });
  }, [details?.scoreInstance?.details]);

  const normalizedScores = useMemo(
    () => normalizeDetailScores(details),
    [details],
  );

  const negativeReplyIndex = useMemo(() => {
    return scoreDetailsSortedBySignificance?.findIndex(
      (detail) => detail.scoreDetailType === 'NegativeReplies',
    );
  }, [scoreDetailsSortedBySignificance]);

  const optOutIndex = useMemo(() => {
    return scoreDetailsSortedBySignificance?.findIndex(
      (detail) => detail.scoreDetailType === 'OptOut',
    );
  }, [scoreDetailsSortedBySignificance]);

  const positiveReplyIndex = useMemo(() => {
    return scoreDetailsSortedBySignificance?.findIndex(
      (detail) => detail.scoreDetailType === 'PositiveReplies',
    );
  }, [scoreDetailsSortedBySignificance]);

  const lastUpdatedText = (() => {
    if (!details?.lastUpdated) return 'Date Unknown';
    const d = new Date(details.lastUpdated);
    const now = new Date();
    // needed because of possible clock skew between the server and the client
    const safeDate = d > now ? now : d;
    return isValid(safeDate)
      ? formatDistanceToNow(safeDate, { addSuffix: true })
      : 'Date Unknown';
  })();

  function handleRefresh(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onRefresh();
  }

  function handleSettings(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onSettings();
  }

  return (
    <div className="flex flex-col overflow-hidden h-full text-sm">
      <div className="flex items-center justify-between pr-4">
        <div
          className={cn(
            'flex flex-col flex-1 px-2 rounded-lg pb-0.5',
            scoreColors.gradientBgToRight,
          )}
        >
          <div
            className={cn(
              'flex items-center gap-1 text-2xl font-medium',
              scoreColors.text,
            )}
          >
            {scoreChangeIcon && (
              <div className="flex items-center justify-center">
                {scoreChangeIcon}
              </div>
            )}

            {details?.currentScore ?? '-'}
          </div>

          <span className="text-gray-700 text-sm">{typeLabel} Score</span>
        </div>

        <div className="flex flex-col">
          <div className="flex items-center justify-end gap-2">
            <Button
              size="xs"
              tone="darkSky"
              variant="light"
              startIcon={RefreshSmIcon}
              loading={refreshLoading}
              onClick={handleRefresh}
            >
              Refresh
            </Button>

            {!hideSettings && (
              <IconButton
                tone="gray"
                size="xs"
                variant="outline"
                icon={SettingsSmIcon}
                label="Settings link"
                onClick={handleSettings}
              />
            )}
          </div>

          <div className="text-neutral-500 text-2xs font-medium my-3">
            Last update: <span>{lastUpdatedText}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col flex-1 overflow-auto gap-2.5 pr-4">
        {children ||
          scoreDetailsSortedBySignificance?.map((detail, index) => (
            <LeadScoreDetailsItem
              key={detail.id}
              detail={detail}
              defaultProperties={hashedDefaultProperties}
              significantNegative={
                negativeReplyIndex === index || optOutIndex === index
              }
              significantPositive={positiveReplyIndex === index}
              insignificant={isInsignificant(
                index,
                negativeReplyIndex,
                optOutIndex,
              )}
              score={detail.pointsAdded}
              normalizedScore={normalizedScores.get(detail.id) ?? 0}
            />
          ))}
      </div>
    </div>
  );
};
